import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../config/apiconfig";

const ReturnContext = createContext();

export const useReturns = () => useContext(ReturnContext);

export const ReturnProvider = ({ children }) => {
  const [returnReasons, setReturnReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reasonsLoading, setReasonsLoading] = useState(false);

  // Fetch return reasons from API
  const fetchReturnReasons = useCallback(async () => {
    setReasonsLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${BASE_URL}/return-reasons`);

      if (response.data.status === 200) {
        const activeReasons = response.data.data.filter(reason => reason.status === 'active');
        setReturnReasons(activeReasons);
      } else {
        throw new Error('Failed to fetch return reasons');
      }
    } catch (err) {
      setError('Failed to load return reasons. Please try again.');
      setReturnReasons([]);
    } finally {
      setReasonsLoading(false);
    }
  }, []);

  // Submit return request
  const submitReturnRequest = useCallback(async (returnData) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (!token || !user) {
        throw new Error("Please login to submit a return request.");
      }

      const userData = JSON.parse(user);
      const userId = userData?.id;

      if (!userId) {
        throw new Error("User ID not found. Please login again.");
      }

      // Create FormData for file uploads
      const formData = new FormData();
      formData.append('order_id', returnData.orderId);
      formData.append('user_id', userId);
      formData.append('reason_id', returnData.reasonId);
      formData.append('description', returnData.description);

      // Add image if provided
      if (returnData.image) {
        formData.append('image', returnData.image);
      }

      // Add video if provided
      if (returnData.video) {
        formData.append('video', returnData.video);
      }


      const response = await axios.post(`${BASE_URL}/product-returns`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });


      if (response.data.status === 201) {
        return {
          success: true,
          message: response.data.message || 'Return request submitted successfully',
          data: response.data
        };
      } else {
        throw new Error(response.data.message || 'Failed to submit return request');
      }
    } catch (err) {

      let errorMessage = 'Failed to submit return request. Please try again.';

      if (err.response?.status === 401) {
        errorMessage = 'Session expired. Please login again.';
      } else if (err.response?.status === 422) {
        errorMessage = 'Invalid data provided. Please check your inputs.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load return reasons on mount
  useEffect(() => {
    fetchReturnReasons();
  }, [fetchReturnReasons]);

  return (
    <ReturnContext.Provider value={{
      returnReasons,
      loading,
      error,
      reasonsLoading,
      submitReturnRequest,
      fetchReturnReasons,
      clearError
    }}>
      {children}
    </ReturnContext.Provider>
  );
};
